/**
 * The rule families, mirrored from the backend registry.
 *
 * ── Why the frontend holds this at all ───────────────────────────────────────
 * The summary payload (`?view=ui`) gives each issue a `code` but NOT a
 * `category`; only the issue-detail endpoint carries one. Grouping the findings
 * list by family therefore needs a local map, and fetching a dossier per issue
 * just to learn its family would defeat the point of the lazy detail fetch.
 *
 * ── Keeping it honest as the backend moves ───────────────────────────────────
 *   - An authoritative `category` from a fetched dossier ALWAYS wins over this
 *     table (`ruleFamily` takes it as its second argument).
 *   - An unrecognised code falls to `other` and is labelled "Other checks". It
 *     is never guessed into a family — a mislabelled finding is worse than an
 *     unlabelled one.
 *   - Development builds warn once per unknown code, so backend drift surfaces
 *     the first time someone opens the console rather than silently.
 *
 * This whole module is retired the day the backend exposes
 * `GET /validation/rules`; nothing else should mirror backend knowledge.
 */

export type RuleFamily = 'field' | 'cross_field' | 'period' | 'presence' | 'other';

/**
 * Every code the ruleset defines, by the module that registers it.
 *
 * 30 rules: 8 field-level, 11 cross-field, 6 cross-year, 5 presence.
 */
export const RULE_FAMILY: Record<string, RuleFamily> = {
  // Field-level — one value, judged on its own.
  FIELD_NEGATIVE: 'field',
  FIELD_NOT_NUMERIC: 'field',
  COMMON_STOCK_VS_FACE_VALUE: 'field',
  SCALE_CONSISTENCY: 'field',
  FIELD_RANGE: 'field',
  SHARE_COUNT_INVALID: 'field',
  FUTURE_YEAR: 'field',
  DATE_INVALID: 'field',

  // Cross-field — an identity or a relationship between figures.
  BS_IDENTITY: 'cross_field',
  CROSS_STATEMENT_SCALE: 'cross_field',
  PL_DISTINCTNESS: 'cross_field',
  BS_ROLLUP: 'cross_field',
  PL_IDENTITY: 'cross_field',
  CF_RECONCILIATION: 'cross_field',
  EPS_ORDERING: 'cross_field',
  FCF_RECONCILIATION: 'cross_field',
  EPS_VS_NET_INCOME: 'cross_field',
  MARKET_CAP_VS_PRICE_SHARES: 'cross_field',
  CASH_EXCEEDS_ASSETS: 'cross_field',

  // Cross-year — this year against the ones before it.
  YOY_SCALE_MULTIPLE: 'period',
  YOY_SWING: 'period',
  YOY_SIGN_FLIP: 'period',
  STATIC_FIELD: 'period',
  PERIOD_GAP: 'period',
  PERIOD_TYPE_MISMATCH: 'period',

  // Presence — what the source did or did not supply.
  CRITICAL_FIELD_MISSING: 'presence',
  SUPPORTING_FIELD_MISSING: 'presence',
  ZERO_AS_MISSING: 'presence',
  STATEMENT_ABSENT: 'presence',
  COVERAGE_LOW: 'presence',
};

/**
 * Codes the backend treats as AMBIGUOUS: they can fire on a genuine business
 * fact as easily as on an extraction fault, so their score weight is discounted
 * and they are the findings the AI reviewer is asked about.
 *
 * Surfaced in the UI so a reader understands why one warning got an AI opinion
 * and another did not.
 */
export const AMBIGUOUS_CODES = new Set([
  'BS_IDENTITY',
  'PL_IDENTITY',
  'YOY_SWING',
  'YOY_SIGN_FLIP',
  'STATIC_FIELD',
]);

/** The backend's `Category` values, mapped onto our display families. */
const CATEGORY_TO_FAMILY: Record<string, RuleFamily> = {
  field: 'field',
  invalid: 'field',
  cross_field: 'cross_field',
  period_over_period: 'period',
  missing: 'presence',
};

const warned = new Set<string>();

/**
 * The family a finding belongs to.
 *
 * @param code     the rule code from the summary payload
 * @param category the authoritative `category` from a fetched dossier, when the
 *                 caller happens to have one — it always wins over the mirror
 */
export function ruleFamily(
  code: string | null | undefined,
  category?: string | null
): RuleFamily {
  if (category) {
    const mapped = CATEGORY_TO_FAMILY[category];
    if (mapped) return mapped;
  }
  if (!code) return 'other';

  const known = RULE_FAMILY[code];
  if (known) return known;

  if (import.meta.env?.DEV && !warned.has(code)) {
    warned.add(code);
    // Not an error: a new backend rule SHOULD degrade to "Other checks" rather
    // than break the console. This just makes the drift visible.
    console.warn(
      `[dataQuality] rule code "${code}" is not in RULE_FAMILY — grouped under "Other checks". ` +
        'Add it to dqRules.ts when convenient.'
    );
  }
  return 'other';
}

export function isAmbiguous(code: string | null | undefined): boolean {
  return !!code && AMBIGUOUS_CODES.has(code);
}

interface FamilyMeta {
  label: string;
  /** One line, for the facet rail's tooltip. */
  hint: string;
}

const FAMILY_META: Record<RuleFamily, FamilyMeta> = {
  field: {
    label: 'Individual values',
    hint: 'One figure judged on its own — out of range, not a number, an impossible date.',
  },
  cross_field: {
    label: 'Figures against each other',
    hint: 'Identities and relationships: the balance sheet balancing, EPS against net income.',
  },
  period: {
    label: 'Year against year',
    hint: 'How a figure moved between reporting periods — scale jumps, sign flips, frozen values.',
  },
  presence: {
    label: 'What was supplied',
    hint: 'Fields and statements the source did or did not provide.',
  },
  other: {
    label: 'Other checks',
    hint: 'Checks this build does not have a grouping for.',
  },
};

/** Display order — the reader's questions, roughly in the order they ask them. */
export const FAMILY_ORDER: RuleFamily[] = [
  'presence',
  'cross_field',
  'field',
  'period',
  'other',
];

export function familyLabel(family: RuleFamily): string {
  return FAMILY_META[family].label;
}

export function familyHint(family: RuleFamily): string {
  return FAMILY_META[family].hint;
}
